import EventDispatcher from '../_utils/EventDispatcher';
export default class Uploader extends EventDispatcher {
  constructor(globalOptions = {}, defaultOptions = {}) {
    super();

    // Overwrite default options with given.
    globalOptions = Object.assign({
      maxConcurrency: 1
    }, globalOptions);
    defaultOptions = Object.assign({
      directory: null,
      disposition: 'upload-only',
      maxChunkSize: 1024 * 1024,
      // One megabyte.
      method: 'post'
    }, defaultOptions);

    // Amount of files currently being transferred.
    let concurrencyCount = 0;
    // If upload is called too often and the concurrency limit is reached then we store the data here for later.
    const uploadQueue = [];
    const tryDequeue = () => {
      // Check if we can dequeue.
      if (uploadQueue.length < 1 || concurrencyCount >= globalOptions.maxConcurrecy) {
        return;
      }

      // Initiate an upload.
      const {
        file,
        options
      } = uploadQueue.shift();
      upload(file, options);
      this.dispatchEvent('dequeue', {
        file: file
      });
    };
    const upload = (file, options) => {
      concurrencyCount++;

      // Dispatch staring event.
      this.dispatchEvent('starting', {
        file: file
      });

      // Keep track of transferred bytes and create the file reader.
      let chunkCount = Math.ceil(file.size / options.maxChunkSize);
      let chunkIndex = -1;
      let fileDownloadPath = null;
      let fileTransferId = null;
      let fileTransferPath = null;
      let fileTransferred = 0;
      let uploadIntent = options.intent;
      let hasTransferred = false;
      const reader = new FileReader();

      // Handle errors.
      reader.addEventListener('error', () => {
        this.dispatchEvent('error', {
          file: file,
          message: 'Reader error encountered.'
        });
      });

      // Whenever data is loaded transfer it.
      reader.addEventListener('loadend', event => {
        if (event.target.readyState !== FileReader.DONE) {
          return;
        }

        // Increment chunk counter.
        chunkIndex++;
        const chunkSize = event.loaded;

        // Create data body.
        let body = {
          uploadIntent: uploadIntent,
          fileDirectoryId: options.directory,
          fileDisposition: options.disposition,
          fileDownloadPath: fileDownloadPath,
          fileName: file.name,
          fileSize: file.size,
          fileTransferId: fileTransferId,
          fileTransferPath: fileTransferPath,
          fileTransferred: fileTransferred,
          fileType: file.type,
          chunkCount: chunkCount,
          chunkData: event.target.result,
          chunkIndex: chunkIndex,
          chunkSize: chunkSize
        };
        body = JSON.stringify(body);
        const request = new XMLHttpRequest();
        request.open('POST', options.url, true);
        // Send the proper header information along with the request
        request.setRequestHeader('Content-type', 'application/json; charset=UTF-8');
        request.addEventListener('readystatechange', () => {
          if (request.readyState != 4) {
            return;
          }
          let data = null;
          try {
            data = JSON.parse(request.responseText);
          } catch {
            this.dispatchEvent('error', {
              file: file,
              message: 'Response error encountered.'
            });
            concurrencyCount--;
            tryDequeue();
            return;
          }
          if (data.error && data.error) {
            this.dispatchEvent('error', {
              file: file,
              message: data.error.message
            });
            concurrencyCount--;
            tryDequeue();
            return;
          }
          if (request.status !== 200) {
            this.dispatchEvent('error', {
              file: file,
              message: 'Upload error encountered.'
            });
            concurrencyCount--;
            tryDequeue();
            return;
          }

          // Store transfer id after first chunk.
          if (fileTransferId === null) {
            fileDownloadPath = data.fileDownloadPath;
            fileTransferId = data.fileTransferId;
            fileTransferPath = data.fileTransferPath;
          }
          uploadIntent = data.uploadIntent;

          // Update transferred and continue chunkin' until we're done.
          fileTransferred += chunkSize;
          if (fileTransferred >= file.size) {
            fileTransferred = file.size;
          }
          if (!hasTransferred) {
            // Dispatch progress event.
            this.dispatchEvent('started', {
              downloadPath: fileDownloadPath,
              file: file,
              transferId: fileTransferId,
              transferPath: fileTransferPath,
              transferred: fileTransferred
            });

            // Mark as having transferred.
            hasTransferred = true;
          }

          // Dispatch progress event.
          this.dispatchEvent('progress', {
            downloadPath: fileDownloadPath,
            file: file,
            transferId: fileTransferId,
            transferPath: fileTransferPath,
            transferred: fileTransferred
          });

          // Continue uploading if not done.
          if (fileTransferred < file.size) {
            uploadChunk();
            return;
          }
          // We are done with this file!

          // Reduce concurrency counter and check if we can dequeue an upload.
          concurrencyCount--;
          tryDequeue();

          // Dispatch done event.
          this.dispatchEvent('finished', {
            downloadPath: fileDownloadPath,
            file: file,
            transferId: fileTransferId,
            transferPath: fileTransferPath,
            transferred: file.size
          });
        });

        // Send!
        request.send(body);
      });

      // Function called recursively starting a new chuck read and upload.
      const uploadChunk = () => {
        // Calculate the new end point of the chunk. Since slice's second parameter is exclusive we need to add one to it to make it inclusive!
        let transferredEnd = fileTransferred + options.maxChunkSize + 1;
        if (transferredEnd > file.size) {
          transferredEnd = file.size;
        }
        const blob = file.slice(fileTransferred, transferredEnd);
        reader.readAsDataURL(blob);
      };

      // Start chunkin'.
      uploadChunk();
    };
    this.upload = (file, options = {}) => {
      // Combine options with default options and validate it.
      options = Object.assign({}, defaultOptions, options);
      if (!options.url || typeof options.url !== 'string') {
        this.dispatchEvent('error', {
          file: file,
          message: 'Required option "url" not set.'
        });
        return false;
      }
      if (!options.intent || typeof options.intent !== 'string') {
        this.dispatchEvent('error', {
          file: file,
          message: 'Required option "intent" not set.'
        });
        return false;
      }

      // Store in queue if concurrency is exceeded.
      if (concurrencyCount >= globalOptions.maxConcurrency) {
        uploadQueue.push({
          file: file,
          options: options
        });
        this.dispatchEvent('queue', {
          file: file
        });
        return true;
      }
      upload(file, options);
      return true;
    };
  }
}